import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { validateSetupToken, setPassword } from '../services/api';

export default function SetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');

  const [password, setPasswordVal] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const data = await validateSetupToken(token);
      if (data.valid) {
        setTokenValid(true);
        setUserInfo(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'This link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPw) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await setPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        fontFamily: 'Manrope, system-ui, sans-serif',
      }}>
      <div className="w-full max-w-sm mx-4"
        style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
          padding: '48px 36px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}>
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
            style={{ background: success ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
            {success ? <CheckCircle className="h-7 w-7 text-white" /> : <Lock className="h-7 w-7 text-white" />}
          </div>
        </div>
        <h1 className="text-center text-2xl font-bold text-slate-100 mb-1">HireFast AI</h1>

        {loading ? (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mb-3" />
            <p className="text-sm text-slate-400">Validating your link...</p>
          </div>
        ) : success ? (
          <div className="text-center py-4">
            <p className="text-sm text-emerald-400 mb-4">Your password has been set successfully!</p>
            <Button onClick={() => navigate('/login')}
              className="w-full h-11 font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
              data-testid="go-to-login-btn">
              Go to Sign In
            </Button>
          </div>
        ) : !tokenValid ? (
          <div className="text-center py-4">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <p className="text-sm text-red-400 mb-4">{error}</p>
            <p className="text-xs text-slate-500">Please contact your admin to resend the setup email.</p>
          </div>
        ) : (
          <>
            <p className="text-center text-sm text-slate-400 mb-6">
              Set a password for <span className="text-slate-200 font-medium">{userInfo?.email}</span>
            </p>
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5 text-slate-500" /> New Password
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPasswordVal(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      autoFocus
                      data-testid="setup-password-input"
                      className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11 pr-10"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5 text-slate-500" /> Confirm Password
                  </Label>
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    placeholder="Re-enter password"
                    required
                    data-testid="setup-confirm-password-input"
                    className="bg-[#0f172a] border-slate-600 text-slate-100 placeholder:text-slate-600 h-11"
                  />
                </div>
              </div>

              {error && (
                <p className="text-red-400 text-sm mt-4 text-center" data-testid="setup-error">{error}</p>
              )}

              <Button type="submit" disabled={saving}
                className="w-full mt-6 h-11 font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                data-testid="setup-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {saving ? 'Setting password...' : 'Set Password'}
              </Button>
            </form>
          </>
        )}

        <p className="text-center text-[11px] text-slate-600 mt-6">
          Powered by TekWissen
        </p>
      </div>
    </div>
  );
}
